import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-brand-950 flex items-center justify-center p-6">
                    <div className="bg-brand-900 border border-red-500/20 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="text-red-500 w-8 h-8" />
                        </div>
                        <h1 className="text-2xl font-serif text-brand-50 mb-3">Something went wrong</h1>
                        <p className="text-brand-300 mb-8 leading-relaxed">
                            We encountered an unexpected error. Don't worry, your data is safe.
                        </p>

                        <div className="bg-black/40 rounded-lg p-4 mb-8 text-left overflow-auto max-h-32 custom-scrollbar">
                            <p className="text-xs font-mono text-red-400 break-words">
                                {this.state.error?.message || 'Unknown technical error'}
                            </p>
                        </div>

                        <button
                            onClick={this.handleReset}
                            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-gold-600 to-gold-500 text-brand-950 font-bold py-3 rounded-xl hover:shadow-lg hover:shadow-gold-500/20 transition-all active:scale-95"
                        >
                            <RefreshCw size={20} />
                            Rejuvenate App
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
